import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Container,
  Stack,
  Grid,
  CardContent,
  Box,
  Tooltip,
  Fab,
  Paper,
  Switch,
  FormControlLabel,
  Badge,
} from '@mui/material';
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Inventory as InventoryIcon,
  Delete as DeleteIcon,

  Clear as ClearIcon,
  Group as GroupIcon,

} from '@mui/icons-material';
import { supabase } from '../lib/supabaseClient';
import { enhancedRealtimeService } from '../services/EnhancedRealtimeService';
import { sanitizeInput } from '../lib/validation';
import { useAuth } from '../contexts/AuthContext';

interface Prop {
  id: string;
  name: string;
  show: string;
  location: string;
  status: 'in use' | 'available' | 'fixing';
  user_group: string;
}

interface UserData {
  user_group: string;
  user_role: string;
}

const statusColors = {
  'in use': 'success',
  fixing: 'warning',
  available: 'info',
} as const;

const allowedStatuses = ['in use', 'available', 'fixing'];
const userGroups = [
  'OS Team',
  'Show Support',
  'Show Management (Bay)',
  'Show Management (Padang)',
  'Artiste Management',
  'Log & Finance',
  'Ops. Safety and Contingencies'
];

export default function Logistics() {
  const { user } = useAuth();
  const [props, setProps] = useState<Prop[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newProp, setNewProp] = useState<Partial<Prop>>({
    name: '',
    show: '',
    status: 'available',
    location: '',
    user_group: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingProp, setEditingProp] = useState<Prop | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propToDelete, setPropToDelete] = useState<Prop | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [filterUserGroup, setFilterUserGroup] = useState<string>('All');
  const [filterStatus, setFilterStatus] = useState<string>('All');
  const [showOnlyMyGroup, setShowOnlyMyGroup] = useState(false);
  
  // Enhanced real-time state
  const [realtimeStatus, setRealtimeStatus] = useState<'connected' | 'connecting' | 'disconnected'>('disconnected');
  const realtimeRef = useRef<any>(null);



  const fetchProps = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('props')
        .select('*')
        .order('id');

      if (error) throw error;
      setProps(data || []);
    } catch (error) {
      setError('Failed to fetch props');
      console.error('Error fetching props:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Enhanced real-time subscription setup
  const setupRealtimeSubscription = useCallback(async () => {
    try {
      realtimeRef.current = await enhancedRealtimeService.subscribeToChannel({
        table: 'props',
        event: '*',
        immediateUpdate: true,
        callback: (payload) => {
          console.log('Logistics real-time update received:', payload);
          
          // Immediate optimistic update for instant UI response
          if (payload.eventType === 'INSERT' && payload.newRecord) {
            setProps(prev => {
              // Check if item already exists to prevent duplicates
              const exists = prev.some(prop => prop.id === payload.newRecord.id);
              if (exists) {
                console.log('Logistics: Item already exists, skipping duplicate');
                return prev;
              }
              console.log('Logistics: Adding new item to props');
              return [payload.newRecord, ...prev];
            });
          } else if (payload.eventType === 'UPDATE' && payload.newRecord) {
            setProps(prev => prev.map(prop => 
              prop.id === payload.newRecord.id ? payload.newRecord : prop
            ));
          } else if (payload.eventType === 'DELETE' && payload.oldRecord) {
            setProps(prev => prev.filter(prop => prop.id !== payload.oldRecord.id));
          }
          
          // Clear optimistic update after 200ms for faster cleanup
          setTimeout(() => {
            // setOptimisticUpdates(prev => { // Removed unused variable
            //   const newSet = new Set(prev);
            //   if (payload.newRecord) newSet.delete(payload.newRecord.id);
            //   if (payload.oldRecord) newSet.delete(payload.oldRecord.id);
            //   return newSet;
            // });
          }, 200); // Reduced from 500ms to 200ms
        }
      });
      
      setRealtimeStatus('connected');
    } catch (error) {
      console.error('Error setting up real-time subscription:', error);
      setRealtimeStatus('disconnected');
    }
  }, []);

  // Listen for real-time events
  useEffect(() => {
    const handleRealtimeEvent = (event: CustomEvent) => {
      const { table, updates } = event.detail;
      if (table === 'props') {
        console.log('Processing real-time updates for props:', updates);
        // Additional processing if needed
      }
    };

    window.addEventListener('realtime-insert', handleRealtimeEvent as EventListener);
    window.addEventListener('realtime-update', handleRealtimeEvent as EventListener);
    window.addEventListener('realtime-delete', handleRealtimeEvent as EventListener);

    return () => {
      window.removeEventListener('realtime-insert', handleRealtimeEvent as EventListener);
      window.removeEventListener('realtime-update', handleRealtimeEvent as EventListener);
      window.removeEventListener('realtime-delete', handleRealtimeEvent as EventListener);
    };
  }, []);

  useEffect(() => {
    fetchUserData();
    fetchProps();
    setupRealtimeSubscription();
    
    // Cleanup on unmount
    return () => {
      if (realtimeRef.current) {
        enhancedRealtimeService.cleanup();
      }
    };
  }, [fetchProps, setupRealtimeSubscription]);

  const fetchUserData = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('users')
        .select('user_group, user_role')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setUserData(data);
      // Set default user_group for new props
      if (data?.user_group) {
        setNewProp(prev => ({ ...prev, user_group: data.user_group }));
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const handleAddProp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Sanitize and validate inputs
    const name = sanitizeInput(newProp.name || '');
    const show = sanitizeInput(newProp.show || '');
    const location = sanitizeInput(newProp.location || '');
    const status = newProp.status || 'available';
    const user_group = newProp.user_group || '';
    
    if (!name || !show || !location || !user_group) {
      setError('All fields are required.');
      return;
    }
    if (!allowedStatuses.includes(status)) {
      setError('Invalid status.');
      return;
    }
    if (!userGroups.includes(user_group)) {
      setError('Invalid user group.');
      return;
    }

    // Check permissions
    if (userData?.user_role !== 'god' && userData?.user_group !== user_group) {
      setError('You can only add props for your own user group.');
      return;
    }

    // Generate new prop ID by finding the highest existing ID
    const existingIds = props.map(prop => {
      const match = prop.id.match(/PROP(\d+)/);
      return match ? parseInt(match[1]) : 0;
    });
    const maxId = Math.max(0, ...existingIds);
    const newId = `PROP${String(maxId + 1).padStart(3, '0')}`;

    try {
      const newPropData = {
        id: newId,
        name,
        show,
        location,
        status,
        user_group,
      };

      // Optimistic update for instant UI response
      setProps(prev => [newPropData, ...prev]);
      // setOptimisticUpdates(prev => new Set([...prev, newId])); // Removed unused variable

      const { error } = await supabase
        .from('props')
        .insert([newPropData]);

      if (error) {
        // Handle duplicate key error specifically
        if (error.code === '23505') {
          throw new Error('A prop with this ID already exists. Please try again.');
        }
        throw error;
      }

      setSuccess('Prop added successfully');
      setNewProp({
        name: '',
        show: '',
        status: 'available',
        location: '',
        user_group: userData?.user_group || '',
      });
      setShowAddForm(false);
      
      // Clear optimistic update after successful add
      setTimeout(() => {
        // setOptimisticUpdates(prev => { // Removed unused variable
        //   const newSet = new Set(prev);
        //   newSet.delete(newId);
        //   return newSet;
        // });
      }, 2000);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to add prop');
      console.error('Error adding prop:', error);
      
      // Rollback optimistic update on error
      setProps(prev => prev.filter(prop => prop.id !== newId));
    }
  };

  const handleUpdateStatus = async () => {
    if (!editingProp) return;
    setError(null);
    // Sanitize and validate status
    const status = editingProp.status;
    if (!allowedStatuses.includes(status)) {
      setError('Invalid status.');
      return;
    }

    // Check permissions
    if (userData?.user_role !== 'god' && userData?.user_group !== editingProp.user_group) {
      setError('You can only update props for your own user group.');
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase
        .from('props')
        .update({ status })
        .eq('id', editingProp.id);

      if (error) throw error;

      setSuccess('Prop status updated successfully');
      setUpdateDialogOpen(false);
      setEditingProp(null);
      fetchProps();
    } catch (error) {
      setError('Failed to update prop status');
      console.error('Error updating prop status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProp = async () => {
    if (!propToDelete) return;
    setError(null);

    // Check permissions
    if (userData?.user_role !== 'god' && userData?.user_group !== propToDelete.user_group) {
      setError('You can only delete props for your own user group.');
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase
        .from('props')
        .delete()
        .eq('id', propToDelete.id);

      if (error) throw error;

      setSuccess('Prop deleted successfully');
      setDeleteDialogOpen(false);
      setPropToDelete(null);
      fetchProps();
    } catch (error) {
      setError('Failed to delete prop');
      console.error('Error deleting prop:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (prop: Prop) => {
    setEditingProp(prop);
    setUpdateDialogOpen(true);
  };

  const handleDeleteClick = (prop: Prop) => {
    setPropToDelete(prop);
    setDeleteDialogOpen(true);
  };

  const canEditProp = (prop: Prop) => {
    return userData?.user_role === 'god' || userData?.user_group === prop.user_group;
  };

  const canDeleteProp = (prop: Prop) => {
    return userData?.user_role === 'god' || userData?.user_group === prop.user_group;
  };

  const filteredProps = props.filter(prop => {
    const matchesUserGroup = filterUserGroup === 'All' || prop.user_group === filterUserGroup;
    const matchesStatus = filterStatus === 'All' || prop.status === filterStatus;
    const matchesMyGroup = !showOnlyMyGroup || prop.user_group === userData?.user_group;
    return matchesUserGroup && matchesStatus && matchesMyGroup;
  });

  const clearFilters = () => {
    setFilterUserGroup('All');
    setFilterStatus('All');
    setShowOnlyMyGroup(false);
    setSearchQuery('');
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} sx={{ position: 'relative' }}>
                            <Box display="flex" alignItems="center" gap={2}>
                <InventoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                <Typography variant="h4" fontWeight="bold" color="primary">
                  Logistics Management
                </Typography>
                
                {/* Real-time Status Indicator */}
                <Badge
                  badgeContent=""
                  color={realtimeStatus === 'connected' ? 'success' : realtimeStatus === 'connecting' ? 'warning' : 'error'}
                  sx={{
                    '& .MuiBadge-badge': {
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      animation: realtimeStatus === 'connecting' ? 'pulse 2s infinite' : 'none',
                    }
                  }}
                >
                  <Box sx={{ width: 8, height: 8 }} />
                </Badge>
              </Box>
              <Tooltip title="Add New Prop">
                <Fab
                  color="primary"
                  onClick={() => setShowAddForm(!showAddForm)}
                  sx={{ boxShadow: 3 }}
                >
                  <AddIcon />
                </Fab>
              </Tooltip>
            </Box>
            
            {/* Statistics */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={1} sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {props.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Props
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={1} sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                  <Typography variant="h6" color="success.main" fontWeight="bold">
                    {props.filter(p => p.status === 'available').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Available
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={1} sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                  <Typography variant="h6" color="warning.main" fontWeight="bold">
                    {props.filter(p => p.status === 'in use').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In Use
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={1} sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                  <Typography variant="h6" color="error.main" fontWeight="bold">
                    {props.filter(p => p.status === 'fixing').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Under Repair
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Search and Filters */}
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2} display="flex" alignItems="center" gap={1}>
              <SearchIcon color="primary" />
              Search & Filters
            </Typography>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search Props"
                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    endAdornment: searchQuery && (
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>User Group</InputLabel>
                  <Select
                    value={filterUserGroup}
                    label="User Group"
                    onChange={(e) => setFilterUserGroup(e.target.value)}
                  >
                    <MenuItem value="All">All Groups</MenuItem>
                    {userGroups.map(group => (
                      <MenuItem key={group} value={group}>{group}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="All">All Status</MenuItem>
                    {allowedStatuses.map(status => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showOnlyMyGroup}
                      onChange={(e) => setShowOnlyMyGroup(e.target.checked)}
                    />
                  }
                  label="My Group Only"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                  fullWidth
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Add New Prop Form */}
        {showAddForm && (
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2} display="flex" alignItems="center" gap={1}>
                <AddIcon color="primary" />
                Add New Prop
              </Typography>
              
              <Box component="form" onSubmit={handleAddProp}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Prop Name"
                      variant="outlined"
                      value={newProp.name}
                      onChange={(e) => setNewProp({ ...newProp, name: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Show"
                      variant="outlined"
                      value={newProp.show}
                      onChange={(e) => setNewProp({ ...newProp, show: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label="Location"
                      variant="outlined"
                      value={newProp.location}
                      onChange={(e) => setNewProp({ ...newProp, location: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={newProp.status}
                        label="Status"
                        onChange={(e) => setNewProp({ ...newProp, status: e.target.value as any })}
                      >
                        {allowedStatuses.map(status => (
                          <MenuItem key={status} value={status}>{status}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>User Group</InputLabel>
                      <Select
                        value={newProp.user_group}
                        label="User Group"
                        onChange={(e) => setNewProp({ ...newProp, user_group: e.target.value })}
                        disabled={userData?.user_role !== 'god'}
                      >
                        {userGroups.map(group => (
                          <MenuItem key={group} value={group}>{group}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                
                <Box display="flex" gap={2} mt={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    startIcon={<AddIcon />}
                  >
                    Add Prop
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setShowAddForm(false)}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Props Table */}
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2} display="flex" alignItems="center" gap={1}>
              <InventoryIcon color="primary" />
              Props Inventory ({filteredProps.length} items)
            </Typography>
            
            <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ID</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Show</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Location</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User Group</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProps.map((prop, index) => (
                    <TableRow key={`${prop.id}-${index}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {prop.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {prop.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{prop.show}</TableCell>
                      <TableCell>{prop.location}</TableCell>
                      <TableCell>
                        <Chip
                          label={prop.status}
                          color={statusColors[prop.status]}
                          size="small"
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={prop.user_group}
                          variant="outlined"
                          size="small"
                          icon={<GroupIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Edit Status">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditClick(prop)}
                              disabled={!canEditProp(prop)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Prop">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(prop)}
                              disabled={!canDeleteProp(prop)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Stack>

      {/* Update Status Dialog */}
      <Dialog open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
            Update Prop Status
          </Typography>
        </DialogTitle>
        <DialogContent>
          {editingProp && (
            <Stack spacing={2} mt={1}>
              <Typography variant="body1">
                <strong>Prop:</strong> {editingProp.name} ({editingProp.id})
              </Typography>
              <Typography variant="body1">
                <strong>Current Status:</strong> {editingProp.status}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>New Status</InputLabel>
                <Select
                  value={editingProp.status}
                  label="New Status"
                  onChange={(e) => editingProp && setEditingProp({ ...editingProp, status: e.target.value as any })}
                >
                  {allowedStatuses.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateStatus} variant="contained" disabled={loading}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="body1" fontWeight="bold" color="error" sx={{ fontSize: '1.1rem' }}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          {propToDelete && (
            <Typography variant="body1">
              Are you sure you want to delete <strong>{propToDelete.name}</strong> ({propToDelete.id})?
              This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteProp} variant="contained" color="error" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
} 